import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { FaFacebookF, FaTwitter, FaYoutube, FaInstagram, FaLinkedinIn, FaPhone, FaEnvelope, FaMapMarkerAlt, FaArrowRight, FaClock, FaGlobe, FaCode } from 'react-icons/fa';
import { useSite } from '../context/SiteContext';
import { getMediaUrl } from '../services/api';
import api from '../services/api';
import './Footer.css';

const Footer = () => {
  const { config } = useSite();
  const [footerMenus, setFooterMenus] = useState([]);

  useEffect(() => {
    loadFooterMenus();
  }, []);

  const loadFooterMenus = async () => {
    try {
      const response = await api.get('/menus/', { params: { location: 'footer' } });
      setFooterMenus(response.data);
    } catch (error) {
      console.error('Error loading footer menus:', error);
    }
  };

  return (
    <footer className="site-footer">
      <div className="footer-main">
        <Container>
          <Row className="g-4">
            {/* About Section with Logo */}
            <Col lg={3} md={6} className="footer-col">
              <div className="d-flex align-items-center mb-3">
                {config?.logo && (
                  <Link to="/" className="me-3">
                    <img
                      src={getMediaUrl(config.logo)}
                      alt={config.site_name}
                      className="footer-logo"
                      style={{ height: '60px', width: 'auto', objectFit: 'contain' }}
                    />
                  </Link>
                )}
                <h5 className="footer-title mb-0">
                  {config?.site_name}
                </h5>
              </div>
              <p className="footer-text">{config?.site_tagline}</p>
              <div className="social-links">
                <a href={config?.facebook_url || '#'} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="social-icon" title="Facebook">
                  <FaFacebookF />
                </a>
                <a href={config?.twitter_url || '#'} target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="social-icon" title="Twitter">
                  <FaTwitter />
                </a>
                <a href={config?.youtube_url || '#'} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="social-icon" title="YouTube">
                  <FaYoutube />
                </a>
                <a href={config?.instagram_url || '#'} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-icon" title="Instagram">
                  <FaInstagram />
                </a>
                <a href={config?.linkedin_url || '#'} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="social-icon" title="LinkedIn">
                  <FaLinkedinIn />
                </a>
              </div>
            </Col>

            {/* Quick Links */}
            <Col lg={3} md={6} className="footer-col">
              <h5 className="footer-title">
                <FaArrowRight className="me-2" />
                Quick Links
              </h5>
              <ul className="footer-menu">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/services">Services</Link></li>
                <li><Link to="/news">News &amp; Updates</Link></li>
                <li><Link to="/departments">Departments</Link></li>
                <li><Link to="/contact">Contact Us</Link></li>
                {footerMenus.map(menu =>
                  menu.items && menu.items.slice(0, 3).map(item => (
                    <li key={item.id}>
                      <Link to={item.url}>{item.label}</Link>
                    </li>
                  ))
                )}
              </ul>
            </Col>

            {/* Services */}
            <Col lg={3} md={6} className="footer-col">
              <h5 className="footer-title">
                <FaGlobe className="me-2" />
                Services
              </h5>
              <ul className="footer-menu">
                <li><Link to="/services">View All Services</Link></li>
                <li><Link to="/tenders">Tenders</Link></li>
                <li><Link to="/notices">Notices</Link></li>
                <li><Link to="/downloads">Downloads</Link></li>
                <li><Link to="/rti">RTI Application</Link></li>
                <li><Link to="/grievance">Grievance Redressal</Link></li>
              </ul>
            </Col>

            {/* Contact Info */}
            <Col lg={3} md={6} className="footer-col">
              <h5 className="footer-title">
                <FaPhone className="me-2" />
                Contact Info
              </h5>
              <div className="contact-info">
                {config?.address && (
                  <div className="contact-item">
                    <FaMapMarkerAlt className="contact-icon" />
                    <span>{config.address}</span>
                  </div>
                )}
                {config?.contact_phone && (
                  <div className="contact-item">
                    <FaPhone className="contact-icon" />
                    <a href={`tel:${config.contact_phone}`}>{config.contact_phone}</a>
                  </div>
                )}
                {config?.contact_email && (
                  <div className="contact-item">
                    <FaEnvelope className="contact-icon" />
                    <a href={`mailto:${config.contact_email}`}>{config.contact_email}</a>
                  </div>
                )}
                <div className="contact-item">
                  <FaClock className="contact-icon" />
                  <span>Mon - Fri: 9:00 AM - 5:00 PM</span>
                </div>
                {config?.map_embed_url && (
                  <div className="footer-map-wrapper mt-3">
                    <iframe
                      src={
                        config.map_embed_url.includes('<iframe')
                          ? (config.map_embed_url.match(/src="([^"]+)"/) || [])[1]
                          : config.map_embed_url
                      }
                      width="100%"
                      height="150"
                      style={{ border: 0, borderRadius: '8px' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Location Map"
                    ></iframe>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <div className="footer-divider"></div>

      {/* Visitor Counter & Bottom Section */}
      <div className="footer-bottom">
        <Container>
          {config?.visitor_count && (
            <div className="visitor-counter-wrap text-center mb-3">
              <span className="visitor-label">Total Visitors: </span>
              <span className="visitor-count">
                {String(config.visitor_count).padStart(6, '0').split('').map((digit, idx) => (
                  <span key={idx} className="digit">{digit}</span>
                ))}
              </span>
            </div>
          )}
          <Row className="g-3 align-items-center">
            <Col md={4} className="text-center text-md-start">
              <p className="copyright-text">
                &copy; {new Date().getFullYear()} <strong>{config?.site_name}</strong>. All rights reserved.
              </p>
            </Col>
            <Col md={4} className="text-center">
              <p className="developer-credit">
                <FaCode className="me-1" />
                Designed &amp; Developed by{' '}
                <a
                  href="https://aquantix.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="developer-link"
                >
                  Aquantix Technologies
                </a>
              </p>
            </Col>
            <Col md={4} className="text-center text-md-end">
              <div className="footer-links">
                <Link to="/page/privacy-policy">Privacy Policy</Link>
                <span className="divider">•</span>
                <Link to="/page/terms-and-conditions">Terms &amp; Conditions</Link>
                <span className="divider">•</span>
                <Link to="/page/sitemap">Sitemap</Link>
                <span className="divider">•</span>
                <Link to="/page/accessibility">Accessibility</Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Top Footer Link */}
      <div className="footer-scroll-top">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="scroll-top-btn"
          title="Scroll to top"
        >
          <i className="fas fa-chevron-up"></i>
        </button>
      </div>
    </footer>
  );
};

export default Footer;
